import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Shield, Calendar, User, AlertTriangle, Check } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { SignaturePad } from "./SignaturePad";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint 7 — Parental authorization section embedded in the LandingPage
 * checkout. Self-contained: renders all fields, autocomplete, signature pad,
 * acceptance modal, and exposes the full payload via `onChange`.
 *
 * Props:
 *   ticketType: full ticket type (must include requires_parental_auth, age_category, age_min/max)
 *   sessionId:  current session id (for adult lookup)
 *   show:       show config (parental_terms_html, parental_terms_version, accent_color)
 *   onChange(payload, valid): called whenever fields change. payload is the
 *                             dict ready to be sent to /api/public/register/v2.
 */
export const ParentalAuthSection = ({ ticketType, sessionId, show, onChange }) => {
  const accent = show?.accent_color || "#f59e0b";
  // Back-compat: derive accepted categories list from `parental_categories`
  // (new field) or fall back to legacy single `age_category` when empty.
  const allowedCategories = (ticketType?.parental_categories && ticketType.parental_categories.length)
    ? ticketType.parental_categories
    : (ticketType?.age_category && ticketType.age_category !== "adult" ? [ticketType.age_category] : []);

  const ageRange = allowedCategories.length === 2
    ? "≤ 17 anos"
    : allowedCategories[0] === "child_under_12" ? "≤ 12 anos"
    : allowedCategories[0] === "teen_13_17" ? "13–17 anos"
    : "Menor";

  // ── State
  const [minor, setMinor] = useState({ full_name: "", dob: "" });
  const [guardian, setGuardian] = useState({
    full_name: "", id_doc: "", contact: "", whatsapp: "", email: "", address: "",
  });
  const [responsible, setResponsible] = useState({
    full_name: "", contact: "", whatsapp: "", email: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState("");
  const [signatureDrawnAt, setSignatureDrawnAt] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // ── Computed age + category from DOB
  const computed = useMemo(() => {
    if (!minor.dob) return { age: null, category: null };
    try {
      const dob = new Date(minor.dob);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      const category = age < 13 ? "child_under_12" : age < 18 ? "teen_13_17" : "adult";
      return { age, category };
    } catch {
      return { age: null, category: null };
    }
  }, [minor.dob]);

  // ── Age validation errors
  const ageError = useMemo(() => {
    if (!computed.age && computed.age !== 0) return null;
    if (ticketType?.age_min != null && computed.age < ticketType.age_min)
      return `Idade ${computed.age} é inferior ao mínimo (${ticketType.age_min}) deste bilhete.`;
    if (ticketType?.age_max != null && computed.age > ticketType.age_max)
      return `Idade ${computed.age} é superior ao máximo (${ticketType.age_max}) deste bilhete.`;
    if (allowedCategories.length && !allowedCategories.includes(computed.category)) {
      const friendly = allowedCategories.map((c) => c === "child_under_12" ? "≤ 12 anos" : "13–17 anos").join(" ou ");
      return `Este bilhete exige autorização parental para ${friendly}, mas a idade calculada (${computed.age}) não se enquadra.`;
    }
    return null;
  }, [computed, ticketType, allowedCategories]);

  // Role label depends on the actual MINOR's age once DOB is filled (since a
  // single ticket type may accept both categories). Defaults to "Responsável"
  // (broader semantic) while DOB is empty.
  const roleLbl = computed.category === "child_under_12" ? "Acompanhante" : "Responsável";

  // ── Validity + emit payload
  const isValid = useMemo(() => {
    if (!minor.full_name.trim() || !minor.dob) return false;
    if (ageError) return false;
    if (!guardian.full_name.trim() || !guardian.id_doc.trim()) return false;
    if (!guardian.contact.trim() || !guardian.email.trim() || !guardian.address.trim()) return false;
    if (!responsible.full_name.trim() || !responsible.contact.trim()) return false;
    if (!accepted) return false;
    if (!signatureUrl) return false;
    return true;
  }, [minor, guardian, responsible, accepted, signatureUrl, ageError]);

  useEffect(() => {
    if (!onChange) return;
    const payload = {
      minor: { full_name: minor.full_name.trim(), dob: minor.dob },
      guardian: { ...guardian, email: guardian.email.trim().toLowerCase() },
      responsible: { ...responsible, email: (responsible.email || "").trim().toLowerCase() },
      acceptance: {
        accepted: accepted,
        terms_version: show?.parental_terms_version || 1,
      },
      signature: { data_url: signatureUrl, drawn_at: signatureDrawnAt || new Date().toISOString() },
    };
    onChange(payload, isValid);
    // We intentionally exclude `onChange` from deps to avoid infinite re-render
    // loops when parent recreates the callback each render. The handler is read
    // from latest closure on every effect run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minor, guardian, responsible, accepted, signatureUrl, signatureDrawnAt, isValid, show?.parental_terms_version]);

  return (
    <div className="border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-950/30 to-zinc-900 rounded-2xl p-5 my-4" data-testid="parental-auth-section">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-5 h-5 text-cyan-400" />
        <h4 className="text-white font-semibold">Autorização Parental Obrigatória</h4>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
          {ageRange}
        </span>
      </div>
      <p className="text-cyan-100/70 text-xs mb-5">
        Este bilhete é para menores. O Encarregado de Educação tem de preencher e assinar este termo de responsabilidade.
      </p>

      {/* DADOS DO MENOR */}
      <div className="mb-5">
        <p className="text-cyan-400 text-[10px] uppercase tracking-[2px] font-semibold mb-2">1. Dados do Menor</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-300 text-xs">Nome completo do menor</Label>
            <Input value={minor.full_name} onChange={(e) => setMinor({ ...minor, full_name: e.target.value })} className="input-dark mt-1" placeholder="Nome do menor" data-testid="parental-minor-name" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Data de nascimento</Label>
            <Input type="date" value={minor.dob} onChange={(e) => setMinor({ ...minor, dob: e.target.value })} className="input-dark mt-1" data-testid="parental-minor-dob" />
            {computed.age != null && !ageError && (
              <p className="text-emerald-300 text-[11px] mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" /> Idade: {computed.age} anos · OK para este bilhete
              </p>
            )}
            {ageError && (
              <p className="text-red-300 text-[11px] mt-1 flex items-start gap-1" data-testid="parental-age-error">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {ageError}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ENCARREGADO DE EDUCAÇÃO */}
      <div className="mb-5">
        <p className="text-cyan-400 text-[10px] uppercase tracking-[2px] font-semibold mb-2">2. Encarregado de Educação</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-300 text-xs">Nome completo do EE</Label>
            <Input value={guardian.full_name} onChange={(e) => setGuardian({ ...guardian, full_name: e.target.value })} className="input-dark mt-1" data-testid="parental-guardian-name" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">CC / Doc. Identidade do EE</Label>
            <Input value={guardian.id_doc} onChange={(e) => setGuardian({ ...guardian, id_doc: e.target.value })} className="input-dark mt-1" placeholder="12345678 9 ZZ4" data-testid="parental-guardian-iddoc" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Contacto telefónico do EE</Label>
            <Input value={guardian.contact} onChange={(e) => setGuardian({ ...guardian, contact: e.target.value })} className="input-dark mt-1" placeholder="+351 ..." data-testid="parental-guardian-contact" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">WhatsApp do EE</Label>
            <Input value={guardian.whatsapp} onChange={(e) => setGuardian({ ...guardian, whatsapp: e.target.value })} className="input-dark mt-1" placeholder="+351 ..." data-testid="parental-guardian-whatsapp" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Email do EE</Label>
            <Input type="email" value={guardian.email} onChange={(e) => setGuardian({ ...guardian, email: e.target.value })} className="input-dark mt-1" data-testid="parental-guardian-email" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-xs">Morada completa do EE</Label>
            <Textarea rows={2} value={guardian.address} onChange={(e) => setGuardian({ ...guardian, address: e.target.value })} className="input-dark mt-1" placeholder="Rua, número, código postal, localidade" data-testid="parental-guardian-address" />
          </div>
        </div>
      </div>

      {/* ADULTO RESPONSÁVEL/ACOMPANHANTE — sem pesquisa, campos directos */}
      <div className="mb-5">
        <p className="text-cyan-400 text-[10px] uppercase tracking-[2px] font-semibold mb-2">3. Adulto {roleLbl} (maior de 18 anos)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-xs">Nome completo do {roleLbl}</Label>
            <Input value={responsible.full_name} onChange={(e) => setResponsible({ ...responsible, full_name: e.target.value })} className="input-dark mt-1" data-testid="parental-resp-name" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Contacto telefónico do {roleLbl}</Label>
            <Input value={responsible.contact} onChange={(e) => setResponsible({ ...responsible, contact: e.target.value })} className="input-dark mt-1" placeholder="+351 ..." data-testid="parental-resp-contact" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">WhatsApp do {roleLbl}</Label>
            <Input value={responsible.whatsapp} onChange={(e) => setResponsible({ ...responsible, whatsapp: e.target.value })} className="input-dark mt-1" placeholder="+351 ..." data-testid="parental-resp-whatsapp" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-xs">Email do {roleLbl}</Label>
            <Input type="email" value={responsible.email} onChange={(e) => setResponsible({ ...responsible, email: e.target.value })} className="input-dark mt-1" data-testid="parental-resp-email" />
          </div>
        </div>
      </div>

      {/* ASSINATURA */}
      <div className="mb-5">
        <p className="text-cyan-400 text-[10px] uppercase tracking-[2px] font-semibold mb-2">4. Assinatura do Encarregado</p>
        <SignaturePad value={signatureUrl} onChange={(url) => { setSignatureUrl(url); setSignatureDrawnAt(url ? new Date().toISOString() : null); }} accent={accent} />
      </div>

      {/* ACEITAÇÃO */}
      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-zinc-900/50 border border-white/10 hover:border-cyan-500/30 transition-colors" data-testid="parental-accept-row">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-cyan-500"
          data-testid="parental-accept-checkbox"
        />
        <div className="text-sm text-zinc-200">
          Li, aceito e assino o{" "}
          <button
            type="button"
            onClick={() => setShowTermsModal(true)}
            className="text-cyan-300 underline hover:text-cyan-100"
            data-testid="parental-terms-link"
          >
            Termo de Responsabilidade
          </button>
          {" "}e autorizo a participação do menor.
        </div>
      </label>

      {/* Validity hint */}
      {!isValid && (
        <p className="text-amber-300/70 text-[11px] mt-3 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Preencha todos os campos, assine e aceite o termo para continuar.
        </p>
      )}

      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="parental-terms-modal">
          <DialogHeader>
            <DialogTitle className="text-cyan-300">Termo de Responsabilidade</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Versão v{show?.parental_terms_version || 1}. Leia atentamente antes de aceitar.
            </DialogDescription>
          </DialogHeader>
          <div
            className="prose prose-invert prose-sm max-w-none text-zinc-200"
            dangerouslySetInnerHTML={{ __html: show?.parental_terms_html || "<p><em>(O administrador ainda não definiu o texto do termo. Contacte a organização.)</em></p>" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentalAuthSection;
